import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle, ChevronDown, Copy, ExternalLink, Eye, EyeOff, KeyRound, Loader2, Monitor, Plug, Plus, RefreshCw, Trash2, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { useBotConnectionsQuery } from '../../hooks/queries';
import {
  useConnectService, useDisconnectService, useImportConnections, useReconnectService, useRefreshConnection,
  useUpdateConnection,
} from '../../hooks/mutations';
import { PanelCard, PanelEmpty, PanelIntro, PanelSection, panelButton, panelPrimary } from './panel/PanelKit';
import { startExternalAuth } from '../../native/links';
import { desktop } from '../../native/desktop';
import { connectInDesktop } from '../../lib/desktopSignIn';
import { cn } from '../../lib/utils';

export const RETURN_KEY = 'bb.connect.return';

/** Remember where to come back to, then go and sign in to the service. */
export const goSignIn = (url) => {
  try { sessionStorage.setItem(RETURN_KEY, window.location.pathname + window.location.search); } catch { /* private mode */ }
  startExternalAuth(url);
};

const errorText = (err, t) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return detail?.message || t('bots.connections.failed');
};

const needsDesktop = (err) => err?.response?.data?.detail?.error === 'needs_desktop';

/** A service that lets only desktop apps sign in (Upwork): in the desktop app it signs in there
 * (lib/desktopSignIn.js); on the web the person is told to connect it in the desktop app. */
const useDesktopConnect = (t) => {
  // The API's mutations refresh the bot's connections by themselves (services/api.js).
  return async (args, name, onDone) => {
    if (!desktop.canSignInForApps()) {
      toast.error(t('bots.connections.needs_desktop', { name }));
      return;
    }
    try {
      const conn = await connectInDesktop({ ...args, doneText: t('bots.connections.desktop_done') });
      toast.success(t('bots.connections.connected_toast', { name: conn?.name || name }));
      onDone?.(conn);
    } catch (err) {
      toast.error(err?.message === 'denied' ? t('bots.connections.desktop_denied', { name }) : errorText(err, t));
    }
  };
};

const input = 'w-full text-sm rounded-md border border-border bg-transparent px-2 py-1.5 focus:outline-none focus:border-amber-500/60';

/** Logos of the catalog services (public/integrations, sources in SOURCES.md). */
const LOGO_FILES = {
  google: 'google.png', gmail: 'gmail.svg', google_calendar: 'google_calendar.svg', higgsfield: 'higgsfield.svg', fal: 'fal.png', clickup: 'clickup.svg', notion: 'notion.svg',
  canva: 'canva.svg', figma: 'figma.svg', stripe: 'stripe.svg', paypal: 'paypal.svg', github: 'github.svg',
  cloudflare: 'cloudflare.svg', google_analytics: 'google_analytics.svg', google_search_console: 'google_search_console.png',
  meta_ads: 'meta_ads.svg', ubersuggest: 'ubersuggest.svg', radaar: 'radaar.png', modulards: 'modulards.png', upwork: 'upwork.svg', clarity: 'clarity.png',
  linear: 'linear.svg', airtable: 'airtable.svg', todoist: 'todoist.svg', monday: 'monday.png', smartsheet: 'smartsheet.png', miro: 'miro.svg',
  calendly: 'calendly.svg', calcom: 'calcom.png', uxpilot: 'uxpilot.png', klaviyo: 'klaviyo.png', brevo: 'brevo.svg', typeform: 'typeform.png',
  make: 'make.svg', zapier: 'zapier.svg', activepieces: 'activepieces.svg', webflow: 'webflow.svg', wix: 'wix.svg', wordpress_com: 'wordpress_com.svg',
  hostinger: 'hostinger.svg', netlify: 'netlify.svg', railway: 'railway.svg', supabase: 'supabase.svg', postman: 'postman.svg',
  browserstack: 'browserstack.png', posthog: 'posthog.svg', context7: 'context7.svg', huggingface: 'huggingface.svg', perplexity: 'perplexity.svg',
  apify: 'apify.svg',
};

/** The service's own logo on a white tile (readable in both themes); a plug for other servers. */
const ServiceLogo = ({ id, size = 'md' }) => {
  const box = size === 'sm' ? 'w-7 h-7 rounded-md p-1' : 'w-10 h-10 rounded-lg p-1.5';
  const file = LOGO_FILES[id];
  if (!file) {
    return (
      <span className={`${box} shrink-0 flex items-center justify-center bg-muted text-muted-foreground`}>
        <Plug className="w-4 h-4" />
      </span>
    );
  }
  return (
    <span className={`${box} shrink-0 flex items-center justify-center bg-white border border-black/10`}>
      <img src={`/integrations/${file}`} alt="" className="w-full h-full object-contain" draggable={false} />
    </span>
  );
};

/** Name/value rows (headers or variables). A value is hidden as it is typed and can be shown;
 * a saved one never comes back from the server (write-only). Classes go through cn, so a row's
 * widths replace the input's full width (both used to stay, and the value field shrank to nothing). */
const PairRows = ({ rows, onChange, namePlaceholder, valuePlaceholder, t }) => {
  const [shown, setShown] = useState({});
  return (
  <div className="space-y-1.5">
    {rows.map((row, i) => (
      <div key={i} className="flex items-center gap-1.5">
        <input value={row.name} onChange={(e) => onChange(rows.map((r, j) => (j === i ? { ...r, name: e.target.value } : r)))}
          placeholder={namePlaceholder} className={cn(input, 'w-2/5 shrink-0 font-mono text-xs')} data-testid="pair-name" />
        <div className="relative min-w-0 flex-1">
          <input value={row.value} type={shown[i] ? 'text' : 'password'} autoComplete="off" data-testid="pair-value"
            onChange={(e) => onChange(rows.map((r, j) => (j === i ? { ...r, value: e.target.value } : r)))}
            placeholder={row.saved ? t('bots.connections.value_saved') : valuePlaceholder} className={cn(input, 'pr-7 font-mono text-xs')} />
          <button type="button" onClick={() => setShown({ ...shown, [i]: !shown[i] })}
            aria-label={t(shown[i] ? 'bots.connections.hide_value' : 'bots.connections.show_value')}
            title={t(shown[i] ? 'bots.connections.hide_value' : 'bots.connections.show_value')}
            className="absolute right-1 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground">
            {shown[i] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
        </div>
        <button type="button" onClick={() => onChange(rows.filter((_, j) => j !== i))} aria-label={t('bots.connections.remove')}
          className="p-1 text-muted-foreground hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
      </div>
    ))}
    <button type="button" onClick={() => onChange([...rows, { name: '', value: '' }])}
      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
      <Plus className="w-3 h-3" />{t('bots.connections.add_row')}
    </button>
  </div>
  );
};

const toObject = (rows) => Object.fromEntries(rows.filter((r) => r.name.trim() && r.value).map((r) => [r.name.trim(), r.value]));

const CopyField = ({ value, t }) => (
  <div className="flex items-center gap-1.5">
    <code className="flex-1 min-w-0 truncate rounded bg-muted/60 px-2 py-1 text-xs">{value}</code>
    <button type="button" className="p-1 text-muted-foreground hover:text-foreground" title={t('bots.connections.copy')}
      onClick={() => { navigator.clipboard?.writeText(value); toast.success(t('bots.connections.copied')); }}>
      <Copy className="w-3.5 h-3.5" />
    </button>
  </div>
);

/** How to make one's own app at a known service (backend own_app_help): its steps and docs. */
const OwnAppGuide = ({ help, t }) => (
  <div className="space-y-1" data-testid="own-app-guide">
    <p className="text-xs text-muted-foreground">{t(`bots.connections.own_app_guide.${help.service}`)}</p>
    <a href={help.help_url} target="_blank" rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-xs text-brand-ink hover:underline">
      {t('bots.connections.own_app_docs', { name: help.name })}<ExternalLink className="w-3 h-3" />
    </a>
  </div>
);

/** A server that cannot register BusyBee: the person enters their own app. */
const OwnAppForm = ({ redirectUri, onSubmit, busy, t, submitLabel, help }) => {
  const [form, setForm] = useState({ id: '', secret: '', scopes: '' });
  return (
    <div className="space-y-2" data-testid="own-app-form">
      {help ? <OwnAppGuide help={help} t={t} /> : <p className="text-xs text-muted-foreground">{t('bots.connections.own_app_hint')}</p>}
      {redirectUri && <CopyField value={redirectUri} t={t} />}
      <input value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })} placeholder={t('bots.connections.client_id')} className={input} />
      {help?.secret !== false && (
        <input value={form.secret} type="password" autoComplete="off" onChange={(e) => setForm({ ...form, secret: e.target.value })}
          placeholder={t('bots.connections.client_secret')} className={input} />
      )}
      {help?.scopes !== false && (
        <input value={form.scopes} onChange={(e) => setForm({ ...form, scopes: e.target.value })} placeholder={t('bots.connections.scopes')} className={input} />
      )}
      {onSubmit && (
        <Button type="button" size="sm" disabled={!form.id.trim() || busy} onClick={() => onSubmit(form)}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (submitLabel || t('bots.connections.connect'))}
        </Button>
      )}
    </div>
  );
};

/** Headings of the catalog, in order (the backend's CATEGORIES). */
const CATEGORIES = ['communication', 'projects', 'design', 'marketing', 'sales', 'automation', 'websites', 'development', 'research'];

const CatalogCard = ({ c, busy, onPick, t }) => (
  <button type="button" disabled={!c.available || busy} onClick={() => onPick(c)} data-testid={`catalog-${c.id}`}
    className="flex items-center gap-3 rounded-xl border border-border bg-background px-3 py-2.5 text-left text-sm transition-colors hover:border-amber-500/50 hover:bg-accent/50 disabled:opacity-55 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-border">
    <ServiceLogo id={c.id} />
    <span className="min-w-0 flex-1">
      <span className="block truncate font-medium text-foreground">{c.name}</span>
      <span className="block text-xs text-muted-foreground truncate">
        {c.awaiting_approval ? t('bots.connections.awaiting_approval', { name: c.name })
          : !c.available ? t('bots.connections.unavailable')
            : c.desktop_only && !desktop.canSignInForApps() ? t('bots.connections.desktop_only')
            : t(c.auth === 'token' || (c.env || []).length ? 'bots.connections.with_token'
              : c.auth === 'none' ? 'bots.connections.no_sign_in' : 'bots.connections.with_sign_in')}
        {c.available && (c.skills || []).length > 0 && ` · ${t('bots.connections.with_skills', { name: c.name })}`}
      </span>
    </span>
  </button>
);

/** Catalog services under their category headings; a service without a known one comes last. */
const CatalogGroups = ({ entries, busy, onPick, t }) => {
  const groups = [...CATEGORIES.map((key) => [key, entries.filter((c) => c.category === key)]),
    [null, entries.filter((c) => !CATEGORIES.includes(c.category))]];
  return groups.filter(([, items]) => items.length > 0).map(([key, items]) => (
    <div key={key || 'other'} className="space-y-1.5" data-testid={`catalog-group-${key || 'other'}`}>
      {key && <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t(`bots.connections.category_${key}`)}</h3>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {items.map((c) => <CatalogCard key={c.id} c={c} busy={busy} onPick={onPick} t={t} />)}
      </div>
    </div>
  ));
};

const ConnectDialog = ({ onClose, botId, catalog, redirectUri, isTeam, canShare, projectId, projectName, t }) => {
  const connect = useConnectService();
  const importMutation = useImportConnections();
  const update = useUpdateConnection();
  const reconnect = useReconnectService();
  const [tab, setTab] = useState('catalog');
  const [sharing, setSharing] = useState('personal');  // someone's mailbox is theirs
  const [onlyHere, setOnlyHere] = useState(false);
  const [picked, setPicked] = useState(null);  // a catalog entry that needs a token or keys
  const [pickedValues, setPickedValues] = useState({});
  const [kind, setKind] = useState('remote');
  const [custom, setCustom] = useState({ url: '', transport: '', auth: 'auto', token: '', command: '', name: '' });
  const [headers, setHeaders] = useState([{ name: '', value: '' }]);
  const [envRows, setEnvRows] = useState([{ name: '', value: '' }]);
  const [ownApp, setOwnApp] = useState(null);  // form values for the OAuth app
  const [needsClient, setNeedsClient] = useState(null);  // {connection_id, redirect_uri, message}
  const [config, setConfig] = useState('');
  const [imported, setImported] = useState(null);
  const busy = connect.isPending || importMutation.isPending || update.isPending || reconnect.isPending;
  const desktopConnect = useDesktopConnect(t);

  const done = (res) => {
    if (res.authorize_url) goSignIn(res.authorize_url);
    // A program on the computer starts in the background; its row says when it is ready.
    else if (res.connection?.status === 'starting') onClose();
    else { toast.success(t('bots.connections.connected_toast', { name: res.connection.name })); onClose(); }
  };
  const run = (data, stdio = false) => connect.mutate({
    botId, ...data, sharing, ...((onlyHere || stdio) && projectId ? { project_id: projectId } : {}),
  }, {
    onSuccess: done,
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      if (needsDesktop(err)) {
        desktopConnect({ botId, catalogId: data.catalog_id, sharing, projectId: onlyHere ? projectId : undefined },
          detail.service, onClose);
      } else if (detail?.error === 'needs_client') setNeedsClient(detail);
      else toast.error(errorText(err, t));
    },
  });
  const finishOwnApp = (form) => update.mutate({
    id: needsClient.connection_id, oauth_client_id: form.id, oauth_client_secret: form.secret || undefined,
    oauth_scopes: form.scopes || undefined,
  }, {
    onSuccess: () => reconnect.mutate(needsClient.connection_id, { onSuccess: done, onError: (err) => toast.error(errorText(err, t)) }),
    onError: (err) => toast.error(errorText(err, t)),
  });

  const pick = (c) => {
    if (c.transport === 'stdio' && !projectId) { toast.error(t('bots.connections.needs_project')); return; }
    if (c.auth === 'token' || (c.env || []).length) { setPicked(c); setPickedValues({}); return; }
    run({ catalog_id: c.id }, c.transport === 'stdio');
  };

  const submitCustom = () => {
    if (kind === 'program') {
      run({ command: custom.command.trim(), env: toObject(envRows), name: custom.name || undefined }, true);
      return;
    }
    const data = { url: custom.url.trim(), name: custom.name || undefined, ...(custom.transport ? { transport: custom.transport } : {}) };
    if (custom.auth === 'token' && custom.token.trim()) data.headers = { Authorization: `Bearer ${custom.token.trim()}` };
    if (custom.auth === 'headers') data.headers = toObject(headers);
    if (custom.auth === 'oauth_app' && ownApp) {
      data.oauth_client_id = ownApp.id; data.oauth_client_secret = ownApp.secret || undefined; data.oauth_scopes = ownApp.scopes || undefined;
    }
    run(data);
  };

  const tabs = ['catalog', 'custom', 'import'];
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('bots.connections.dialog_title')}</DialogTitle>
          <DialogDescription>{t('bots.connections.dialog_subtitle')}</DialogDescription>
        </DialogHeader>
        {needsClient ? (
          <div className="space-y-2 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
            <div className="text-sm font-medium">{t('bots.connections.own_app_title')}</div>
            <OwnAppForm redirectUri={needsClient.redirect_uri || redirectUri} onSubmit={finishOwnApp} busy={busy} t={t}
              help={needsClient.own_app} />
            <Button size="sm" variant="ghost" onClick={() => setNeedsClient(null)}>{t('bots.connections.back')}</Button>
          </div>
        ) : (
          <>
            <div className="flex gap-1 border-b border-border" role="tablist">
              {tabs.map((key) => (
                <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
                  data-testid={`connect-tab-${key}`}
                  className={`px-3 py-1.5 text-sm border-b-2 -mb-px ${tab === key ? 'border-amber-500 text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
                  {t(`bots.connections.tab_${key}`)}
                </button>
              ))}
            </div>
            {(
              <div className="space-y-2">
                {projectId && (
                  <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="connect-only-here">
                    <input type="checkbox" className="mt-1 accent-amber-500" checked={onlyHere} onChange={(e) => setOnlyHere(e.target.checked)} />
                    <span>
                      <span className="block font-medium">{t('bots.connections.only_here', { project: projectName || '' })}</span>
                      <span className="block text-xs text-muted-foreground">{t('bots.connections.only_here_hint')}</span>
                    </span>
                  </label>
                )}
                {isTeam && (
                  <fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="connect-sharing">
                    {[['personal', true], ['team', canShare]].map(([key, allowed]) => (
                      <label key={key} className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer ${sharing === key ? 'border-amber-500/60 bg-amber-500/5' : 'border-border'} ${allowed ? '' : 'opacity-50 cursor-not-allowed'}`}>
                        <input type="radio" name="sharing" className="mt-1 accent-amber-500" checked={sharing === key} disabled={!allowed}
                          onChange={() => setSharing(key)} />
                        <span>
                          <span className="block font-medium">{t(`bots.connections.share_${key}`)}</span>
                          <span className="block text-xs text-muted-foreground">
                            {t(allowed ? `bots.connections.share_${key}_hint` : 'bots.connections.share_team_admins')}
                          </span>
                        </span>
                      </label>
                    ))}
                  </fieldset>
                )}
              </div>
            )}

            {tab === 'catalog' && (picked ? (
              <div className="space-y-2 rounded-lg border border-border p-3" data-testid="connect-picked">
                <div className="flex items-center gap-2.5 text-sm font-medium"><ServiceLogo id={picked.id} size="sm" />{picked.name}</div>
                {picked.transport === 'stdio' && <p className="text-xs text-muted-foreground">{t('bots.connections.computer_hint')}</p>}
                {picked.auth === 'token' && (
                  <input type="password" autoComplete="off" value={pickedValues.token || ''} placeholder={t('bots.connections.token')}
                    onChange={(e) => setPickedValues({ ...pickedValues, token: e.target.value })} className={input} />
                )}
                {(picked.env || []).map((name) => (
                  <input key={name} type="password" autoComplete="off" value={pickedValues[name] || ''} placeholder={name}
                    onChange={(e) => setPickedValues({ ...pickedValues, [name]: e.target.value })} className={`${input} font-mono`} />
                ))}
                {t(`bots.connections.token_hint_${picked.id}`, { defaultValue: '' }) && (
                  <p className="text-xs text-muted-foreground whitespace-pre-line" data-testid="connect-token-hint">
                    {t(`bots.connections.token_hint_${picked.id}`)}
                  </p>
                )}
                {picked.token_url && (
                  <a href={picked.token_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-amber-600 hover:underline">
                    {t('bots.connections.get_token')}<ExternalLink className="w-3 h-3" />
                  </a>
                )}
                <div className="flex gap-2">
                  <Button size="sm" disabled={busy || (picked.auth === 'token' ? !pickedValues.token : (picked.env || []).some((n) => !pickedValues[n]))}
                    onClick={() => run(picked.auth === 'token' ? { catalog_id: picked.id, token: pickedValues.token }
                      : { catalog_id: picked.id, env: pickedValues }, picked.transport === 'stdio')}>
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('bots.connections.connect')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setPicked(null)}>{t('bots.connections.back')}</Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <CatalogGroups entries={catalog.filter((c) => !c.admins_only)} busy={busy} onPick={pick} t={t} />
                {catalog.some((c) => c.admins_only) && (
                  <PanelSection title={t('bots.connections.admins_only')} count={catalog.filter((c) => c.admins_only).length}
                    tone="attention" data-testid="catalog-admins-only"
                    className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3">
                    <p className="text-xs text-muted-foreground">{t('bots.connections.admins_only_hint')}</p>
                    <CatalogGroups entries={catalog.filter((c) => c.admins_only)} busy={busy} onPick={pick} t={t} />
                  </PanelSection>
                )}
                {connect.isPending && <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" />{t('bots.connections.connecting')}</p>}
                <p className="text-xs text-muted-foreground" data-testid="catalog-more-coming">{t('bots.connections.more_coming')}</p>
              </div>
            ))}

            {tab === 'custom' && (
              <div className="space-y-3" data-testid="connect-custom">
                <div className="grid grid-cols-2 gap-1.5">
                  {['remote', 'program'].map((k) => (
                    <button key={k} type="button" onClick={() => setKind(k)}
                      className={`rounded-lg border px-3 py-2 text-left text-sm ${kind === k ? 'border-amber-500/60 bg-amber-500/5' : 'border-border'}`}>
                      <span className="flex items-center gap-1.5 font-medium">
                        {k === 'remote' ? <Plug className="w-3.5 h-3.5" /> : <Monitor className="w-3.5 h-3.5" />}{t(`bots.connections.kind_${k}`)}
                      </span>
                      <span className="block text-xs text-muted-foreground">{t(`bots.connections.kind_${k}_hint`)}</span>
                    </button>
                  ))}
                </div>
                {kind === 'remote' ? (
                  <>
                    <input value={custom.url} onChange={(e) => setCustom({ ...custom, url: e.target.value })}
                      placeholder="https://example.com/mcp" className={input} data-testid="custom-url" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <label className="text-xs text-muted-foreground space-y-1">
                        <span>{t('bots.connections.transport')}</span>
                        <select value={custom.transport} onChange={(e) => setCustom({ ...custom, transport: e.target.value })} className={input}>
                          <option value="">{t('bots.connections.transport_auto')}</option>
                          <option value="http">Streamable HTTP</option>
                          <option value="sse">SSE</option>
                        </select>
                      </label>
                      <label className="text-xs text-muted-foreground space-y-1">
                        <span>{t('bots.connections.auth')}</span>
                        <select value={custom.auth} onChange={(e) => setCustom({ ...custom, auth: e.target.value })} className={input} data-testid="custom-auth">
                          {['auto', 'token', 'headers', 'oauth_app'].map((a) => <option key={a} value={a}>{t(`bots.connections.auth_${a}`)}</option>)}
                        </select>
                      </label>
                    </div>
                    {custom.auth === 'token' && (
                      <input type="password" autoComplete="off" value={custom.token} onChange={(e) => setCustom({ ...custom, token: e.target.value })}
                        placeholder={t('bots.connections.token')} className={input} />
                    )}
                    {custom.auth === 'headers' && (
                      <PairRows rows={headers} onChange={setHeaders} namePlaceholder="X-API-Key" valuePlaceholder={t('bots.connections.value')} t={t} />
                    )}
                    {custom.auth === 'oauth_app' && <OwnAppFormLive redirectUri={redirectUri} onChange={setOwnApp} t={t} />}
                  </>
                ) : !projectId ? (
                  <p className="text-sm text-muted-foreground">{t('bots.connections.needs_project')}</p>
                ) : (
                  <>
                    <input value={custom.command} onChange={(e) => setCustom({ ...custom, command: e.target.value })}
                      placeholder="npx -y @modelcontextprotocol/server-filesystem /workspace" className={`${input} font-mono text-xs`} data-testid="custom-command" />
                    <p className="text-xs text-muted-foreground">{t('bots.connections.computer_hint')}</p>
                    <div className="text-xs font-medium">{t('bots.connections.variables')}</div>
                    <PairRows rows={envRows} onChange={setEnvRows} namePlaceholder="API_KEY" valuePlaceholder={t('bots.connections.value')} t={t} />
                  </>
                )}
                <input value={custom.name} onChange={(e) => setCustom({ ...custom, name: e.target.value })} maxLength={60}
                  placeholder={t('bots.connections.name_optional')} className={input} />
                <Button size="sm" onClick={submitCustom} data-testid="custom-connect"
                  disabled={busy || (kind === 'remote' ? !custom.url.trim() : (!projectId || !custom.command.trim()))}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('bots.connections.connect')}
                </Button>
                {connect.isPending && kind === 'program' && <p className="text-xs text-muted-foreground">{t('bots.connections.starting_program')}</p>}
              </div>
            )}

            {tab === 'import' && (
              <div className="space-y-2" data-testid="connect-import">
                <p className="text-xs text-muted-foreground">{t('bots.connections.import_hint')}</p>
                <textarea value={config} onChange={(e) => setConfig(e.target.value)} rows={8} spellCheck={false}
                  placeholder={'{\n  "mcpServers": {\n    "linear": { "url": "https://mcp.linear.app/mcp" },\n    "files": { "command": "npx", "args": ["-y", "@modelcontextprotocol/server-filesystem", "/workspace"] }\n  }\n}'}
                  className={`${input} font-mono text-xs resize-y`} />
                <Button size="sm" disabled={!config.trim() || busy}
                  onClick={() => importMutation.mutate({ botId, config, sharing, only_here: onlyHere, ...(projectId ? { project_id: projectId } : {}) }, {
                    onSuccess: (res) => setImported(res.results),
                    onError: (err) => toast.error(errorText(err, t)),
                  })}>
                  {importMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : t('bots.connections.import')}
                </Button>
                {imported && (
                  <ul className="space-y-1" data-testid="import-results">
                    {imported.map((r, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs">
                        {r.error ? <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" /> : <Plug className="w-3.5 h-3.5 text-emerald-500 shrink-0" />}
                        <span className="font-medium">{r.name}</span>
                        <span className="flex-1 min-w-0 truncate text-muted-foreground">
                          {r.error || (r.needs_sign_in ? t('bots.connections.status_needs_sign_in')
                            : t('bots.connections.tools_count', { count: (r.connection?.tools || []).length }))}
                        </span>
                        {r.needs_sign_in && (
                          <Button size="sm" variant="outline" className="h-6 text-xs"
                            onClick={() => reconnect.mutate(r.connection.id, { onSuccess: done, onError: (err) => toast.error(errorText(err, t)) })}>
                            {t('bots.connections.sign_in')}
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

/** The own-app fields while the person fills in the custom form. */
const OwnAppFormLive = ({ redirectUri, onChange, t }) => {
  const [form, setForm] = useState({ id: '', secret: '', scopes: '' });
  const set = (k) => (e) => { const next = { ...form, [k]: e.target.value }; setForm(next); onChange(next); };
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">{t('bots.connections.own_app_hint')}</p>
      {redirectUri && <CopyField value={redirectUri} t={t} />}
      <input value={form.id} onChange={set('id')} placeholder={t('bots.connections.client_id')} className={input} />
      <input value={form.secret} type="password" autoComplete="off" onChange={set('secret')} placeholder={t('bots.connections.client_secret')} className={input} />
      <input value={form.scopes} onChange={set('scopes')} placeholder={t('bots.connections.scopes')} className={input} />
    </div>
  );
};

const TransportBadge = ({ conn, t }) => {
  const label = conn.transport === 'stdio' ? t('bots.connections.badge_computer') : conn.transport === 'sse' ? 'SSE' : null;
  if (!label) return null;
  return <span className="shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-medium bg-sky-500/15 text-sky-700 dark:text-sky-400">{label}</span>;
};

/** Keys of a connection: header or variable names with write-only values. */
const KeysEditor = ({ conn, onSave, busy, t }) => {
  const stdio = conn.transport === 'stdio';
  const names = stdio ? conn.env_names || [] : conn.header_names || [];
  const [rows, setRows] = useState(names.map((n) => ({ name: n, value: '', saved: true })));
  const [command, setCommand] = useState(stdio ? [conn.command, ...(conn.args || [])].join(' ') : '');
  const save = () => {
    const changes = {};
    rows.forEach((r) => { if (r.name.trim() && r.value) changes[r.name.trim()] = r.value; });
    names.filter((n) => !rows.some((r) => r.name.trim() === n)).forEach((n) => { changes[n] = null; });
    const data = stdio ? { env: changes } : { headers: changes };
    if (stdio && command.trim() !== [conn.command, ...(conn.args || [])].join(' ')) { data.command = command.trim(); data.args = []; }
    onSave(data);
  };
  return (
    <div className="space-y-1.5 rounded border border-border p-2" data-testid="keys-editor">
      {stdio && (
        <input value={command} onChange={(e) => setCommand(e.target.value)} className={`${input} font-mono text-xs`} />
      )}
      <div className="text-[11px] font-medium text-muted-foreground">{t(stdio ? 'bots.connections.variables' : 'bots.connections.headers')}</div>
      <PairRows rows={rows} onChange={setRows} namePlaceholder={stdio ? 'API_KEY' : 'X-API-Key'} valuePlaceholder={t('bots.connections.value')} t={t} />
      <Button size="sm" className="h-7 text-xs" disabled={busy} onClick={save}>{t('bots.connections.save')}</Button>
    </div>
  );
};

const ConnectionRow = ({ conn, canEdit: isAdmin, redirectUri, t }) => {
  // Your own account is yours to manage; the team's accounts are the admins'.
  const canEdit = conn.sharing === 'personal' ? !!conn.mine : isAdmin;
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const refresh = useRefreshConnection();
  const reconnect = useReconnectService();
  const update = useUpdateConnection();
  const disconnect = useDisconnectService();
  const starting = conn.status === 'starting';  // a program on the computer, still starting
  const problem = conn.status !== 'connected' && !starting;
  const policies = conn.tool_policies || {};
  const tools = conn.tools || [];
  const shared = new Set(tools.map((tool) => policies[tool.name] || 'ask'));
  const allPolicy = shared.size === 1 ? [...shared][0] : '';
  const desktopConnect = useDesktopConnect(t);
  const onErr = (err) => (needsDesktop(err) ? desktopConnect({ connectionId: conn.id }, conn.name) : toast.error(errorText(err, t)));
  const setAll = (policy) => update.mutate({ id: conn.id, tool_policies: Object.fromEntries(tools.map((tool) => [tool.name, policy])) },
    { onError: onErr });
  const signIn = () => reconnect.mutate(conn.id, { onSuccess: (r) => (r.authorize_url ? goSignIn(r.authorize_url) : toast.success(t('bots.connections.connected_toast', { name: conn.name }))), onError: onErr });
  return (
    <PanelCard as="li" className="p-0 overflow-hidden">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-accent/40">
        {LOGO_FILES[conn.catalog_id] ? <ServiceLogo id={conn.catalog_id} size="sm" />
          : conn.transport === 'stdio' ? <Monitor className="w-4 h-4 text-emerald-500 shrink-0" /> : <Plug className="w-4 h-4 text-emerald-500 shrink-0" />}
        {problem && <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />}
        {starting && <Loader2 className="w-4 h-4 text-muted-foreground animate-spin shrink-0" />}
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-1.5 text-[13px] font-medium text-foreground min-w-0">
            <span className="truncate">{conn.name}</span>
            <TransportBadge conn={conn} t={t} />
            {conn.sharing === 'personal' && (
              <span className="shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-medium bg-muted text-muted-foreground">
                {t('bots.connections.only_you')}
              </span>
            )}
            {conn.project_id && conn.transport !== 'stdio' && (
              <span className="shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-medium bg-sky-500/15 text-sky-700 dark:text-sky-400">
                {t('bots.connections.this_project')}
              </span>
            )}
          </span>
          <span className="block text-xs text-muted-foreground truncate">
            {starting ? t('bots.connections.starting_program')
              : problem ? (conn.error || t(`bots.connections.status_${conn.status}`))
              : t('bots.connections.tools_count', { count: (conn.tools || []).length })
                + (conn.capabilities?.resources ? ` · ${t('bots.connections.has_resources')}` : '')}
          </span>
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="border-t border-border px-3 py-3 space-y-3">
          <div className="text-xs text-muted-foreground break-all font-mono">
            {conn.transport === 'stdio' ? [conn.command, ...(conn.args || [])].join(' ') : conn.url}
            {conn.server_info?.name ? <span className="font-sans"> · {conn.server_info.name} {conn.server_info.version}</span> : null}
          </div>
          {conn.status === 'needs_client' && canEdit && (
            <OwnAppForm redirectUri={redirectUri} busy={update.isPending || reconnect.isPending} t={t} submitLabel={t('bots.connections.sign_in')}
              help={conn.own_app}
              onSubmit={(form) => update.mutate({ id: conn.id, oauth_client_id: form.id, oauth_client_secret: form.secret || undefined, oauth_scopes: form.scopes || undefined },
                { onSuccess: signIn, onError: onErr })} />
          )}
          {tools.length > 1 && (
            <div className="flex items-center gap-2 text-[13px]">
              <span className="flex-1 min-w-0 truncate font-medium text-foreground">{t('bots.connections.all_tools')}</span>
              <select value={allPolicy} disabled={!canEdit || update.isPending} onChange={(e) => setAll(e.target.value)}
                className="h-7 text-xs rounded-md border border-input bg-background px-2" data-testid="all-tools-policy">
                {!allPolicy && <option value="" disabled>{t('bots.connections.policy_mixed')}</option>}
                {['auto', 'ask', 'off'].map((p) => <option key={p} value={p}>{t(`bots.connections.policy_${p}`)}</option>)}
              </select>
            </div>
          )}
          {tools.length > 0 && (
            <ul className="space-y-1 max-h-60 overflow-y-auto">
              {conn.tools.map((tool) => (
                <li key={tool.name} className="flex items-center gap-2 text-[13px]">
                  <span className="flex-1 min-w-0 truncate text-foreground" title={tool.description}>{tool.title || tool.name}</span>
                  <select value={policies[tool.name] || 'ask'} disabled={!canEdit || update.isPending}
                    onChange={(e) => update.mutate({ id: conn.id, tool_policies: { [tool.name]: e.target.value } }, { onError: onErr })}
                    className="h-7 text-xs rounded-md border border-input bg-background px-2">
                    {['auto', 'ask', 'off'].map((p) => <option key={p} value={p}>{t(`bots.connections.policy_${p}`)}</option>)}
                  </select>
                </li>
              ))}
            </ul>
          )}
          {editing && canEdit && (
            <KeysEditor conn={conn} busy={update.isPending} t={t}
              onSave={(data) => update.mutate({ id: conn.id, ...data }, { onSuccess: () => { setEditing(false); toast.success(t('bots.connections.saved')); }, onError: onErr })} />
          )}
          {canEdit && (
            <div className="flex flex-wrap gap-1.5">
              {problem && conn.status !== 'needs_client' ? (
                <Button size="sm" variant="outline" className={panelButton} disabled={reconnect.isPending} onClick={signIn}>
                  {reconnect.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : t(conn.status === 'needs_sign_in' ? 'bots.connections.sign_in' : 'bots.connections.reconnect')}
                </Button>
              ) : !problem && !starting && (
                <Button size="sm" variant="outline" className={panelButton} disabled={refresh.isPending}
                  onClick={() => refresh.mutate(conn.id, { onError: onErr })}>
                  <RefreshCw className={`w-3 h-3 ${refresh.isPending ? 'animate-spin' : ''}`} />{t('bots.connections.refresh')}
                </Button>
              )}
              {!conn.catalog_id || conn.transport === 'stdio' || (conn.header_names || []).length ? (
                <Button size="sm" variant="outline" className={panelButton} onClick={() => setEditing((e) => !e)} data-testid="edit-keys">
                  <KeyRound className="w-3 h-3" />{t('bots.connections.edit_keys')}
                </Button>
              ) : null}
              <Button size="sm" variant="ghost" className={`${panelButton} text-red-500 hover:text-red-500`}
                disabled={disconnect.isPending} onClick={() => disconnect.mutate(conn.id, { onError: onErr })}>
                <Trash2 className="w-3 h-3" />{t('bots.connections.disconnect')}
              </Button>
            </div>
          )}
        </div>
      )}
    </PanelCard>
  );
};

/** Services (MCP servers) the bot can use, on the bot page. */
const BotConnections = ({ botId, canEdit, isTeam = false, projectId, projectName }) => {
  const { t } = useTranslation();
  // While a program starts on the computer, look again until it is ready or failed (the backend
  // also tells open pages when it is done).
  const { data } = useBotConnectionsQuery(botId, projectId, {
    refetchInterval: (query) => ((query.state.data?.connections || []).some((c) => c.status === 'starting') ? 5000 : false),
  });
  const [adding, setAdding] = useState(false);
  const connections = data?.connections || [];
  return (
    <div className="space-y-3" data-testid="bot-connections">
      <PanelIntro actions={(canEdit || isTeam) && connections.length > 0 && (
        <Button size="sm" className={panelPrimary} onClick={() => setAdding(true)}>
          <Plus className="w-3.5 h-3.5" />{t('bots.connections.add')}
        </Button>
      )}>
        {connections.length > 0 && t('bots.connections.empty_admin')}
      </PanelIntro>
      {connections.length === 0 ? (
        <PanelEmpty icon={Plug} action={(canEdit || isTeam) && (
          <Button size="sm" className={panelPrimary} onClick={() => setAdding(true)}>
            <Plus className="w-3.5 h-3.5" />{t('bots.connections.add')}
          </Button>
        )}>
          {t(canEdit || isTeam ? 'bots.connections.empty_admin' : 'bots.connections.empty')}
        </PanelEmpty>
      ) : (
        <ul className="space-y-2">
          {connections.map((c) => <ConnectionRow key={c.id} conn={c} canEdit={canEdit} redirectUri={data?.redirect_uri} t={t} />)}
        </ul>
      )}
      {adding && (
        <ConnectDialog onClose={() => setAdding(false)} botId={botId} catalog={data?.catalog || []} redirectUri={data?.redirect_uri}
          isTeam={isTeam} canShare={!!canEdit} projectId={projectId} projectName={projectName} t={t} />
      )}
    </div>
  );
};

export default BotConnections;
